import { Button } from 'antd'
import { Flex } from 'uikit/layout/default'
import { CopyToClipboardButton } from 'uikit/buttons/copy-to-clipboard'
import { NodeCategories, type NetworkNodeData } from '../../types'
import { isUrl } from '../../utils/utils'
import styles from '../controls.module.scss'
import { PropertyField } from './property-field'
import { NodeMetadataSection } from './node-metadata-section'

const NAME_TOOLTIP =
  "Name is the primary label for this entity. This field may be empty because the entity was identified through its connections, but its details haven't been collected."

const IDENTIFIER_TOOLTIP =
  'The unique value used to identify this entity. Depending on the type, this could be a URL, email, phone number, wallet address, username, or a system-generated key.'

interface ValueProps {
  value: string
}

function StandardValue({ value }: ValueProps) {
  return <span className={styles.propertyValue}>{value}</span>
}

function IdentifierValue({ value }: ValueProps) {
  if (isUrl(value)) {
    return (
      <a href={value} target='_blank' rel='noopener noreferrer' className={styles.propertyLink}>
        {value}
      </a>
    )
  }

  return <StandardValue value={value} />
}

export interface NodePropertiesProps {
  data: NetworkNodeData
  entityId?: string
  onViewIdentity?: () => void
}

export function NodeProperties({ data, entityId, onViewIdentity }: NodePropertiesProps) {
  const isIdentity = data.nodeCategory === NodeCategories.IDENTITY
  const isMatchingIdentity = isIdentity && !!entityId && data.identifierKey === entityId

  return (
    <Flex className={styles.propertiesContainer}>
      <PropertyField label='Name' tooltip={NAME_TOOLTIP}>
        <StandardValue value={data.displayName || 'N/A'} />
      </PropertyField>

      <PropertyField
        label='Identifier'
        tooltip={IDENTIFIER_TOOLTIP}
        extra={<CopyToClipboardButton text={data.identifierKey} />}
      >
        <IdentifierValue value={data.identifierKey} />
      </PropertyField>

      <NodeMetadataSection nodeType={data.nodeType} metadata={data.metadata} />

      {isMatchingIdentity && onViewIdentity && (
        <Button type='default' onClick={onViewIdentity} className={styles.viewIdentityButton}>
          View Identity Details
        </Button>
      )}
    </Flex>
  )
}
